/* eslint-disable max-len */
/* eslint-disable complexity */
/* eslint-disable no-console */
/* eslint-disable max-lines */
import {makeObservable, observable, flow, computed, action} from 'mobx';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {
    doneJob,
    fetchJobStatus,
    pauseJob,
    fetchJobReport,
    rebuildJob,
    cancelJob,
    continueJob,
    getStageScore,
    terminateJob,
    getStageResultInfo,
    nextJob
} from '@/api/console';

import {
    DefaultFilter,
    EvolveProgressData,
    ColdStartProgressData
} from '@/dicts/taskRuning';
import {
    getProgressPercentage,
    applyStageFilter,
    normalizeSolutions,
    formatSecondsToHMS,
    buildStageData,
    buildEvolveStageParams,
    generateUniqueIslandList
} from '@/utils';
import {handleRequestError} from '@/utils/errorHandler';

import {PollingService} from './PollingService';
import {TaskExecutionStore} from './taskRunning/TaskExecutionStore';
import {StageDataCache} from './taskRunning/StageDataCache';
import {OperationLoadingStore} from './taskRunning/OperationLoadingStore';
import {ConfirmDialogStore} from './taskRunning/ConfirmDialogStore';
import {ViewStateStore} from './taskRunning/ViewStateStore';
import {TaskInitializationStore} from './taskRunning/TaskInitializationStore';
import {TaskEvoRunStore} from './TaskEvoRunStore';
import {TaskCodePackageStore} from './TaskCodePackageStore';
import type {JobsListStore} from './jobs/JobsListStore';

export class TaskRunningStore {
    /** 任务执行状态管理 (替代原来的 status, stage, percentage 等) */
    taskExecutionStore: TaskExecutionStore;
    /** 阶段数据缓存 (替代原来的 stage_data, evolveStageData, resultStageData) */
    stageDataCache: StageDataCache;
    /** 操作加载状态管理 (替代原来的 loading, pauseTaskLoading 等) */
    operationLoadingStore: OperationLoadingStore;
    /** 确认弹窗状态管理 (替代原来的 showSolutionModal, showJobConfigPage, terminateModalVisible) */
    confirmDialogStore: ConfirmDialogStore;
    /** 视图状态管理 (替代原来的 processActivTab, hasNewSolution, heroInputValue, stageInfoName, islandFull) */
    viewStateStore: ViewStateStore;
    /** 任务初始化与清理管理 (替代原来的 hasInitialized, initTaskRunningStore, cancelAllJobRequests) */
    taskInitializationStore: TaskInitializationStore;
    /** 任务续跑管理 (管理"选取优质解继续演化"功能的解选择, 以及一些配置) */
    taskEvoRunStore: TaskEvoRunStore;
    /** 下载代码抽屉状态管理 */
    taskCodePackageStore: TaskCodePackageStore;

    // ==================== 保留的字段 ====================
    job_id: string = '';
    /** 任务web ide链接 */
    web_ide_url: string = '';
    real_web_ide_url: string = '';
    /** 任务执行结果bos 链接 */
    report_url: string = '';
    /** 任务运行结果筛选参数 */
    filter: IFilter = DefaultFilter;

    /** 所有任务阶段的进度信息 */
    allProgressInfo: Partial<ExtraData> = [];
    /** 冷启动阶段基本信息 */
    coldStartProgressData: Partial<ExtraData> = ColdStartProgressData;
    /** 进化任务进度基本信息 */
    evolveProgressData: Partial<ExtraData> = EvolveProgressData;

    /** 阶段详细结果信息 */
    stageInfo: ExtraData | null = null;
    /** 解信息列表 */
    initialSolutionFileInfoList: InitialSolutionFileInfo[] = [];
    /** hover选中的阶段详细结果信息 */
    selectedInitialSolutionRowInfo: InitialSolutionFileInfo | null = null;
    /** 岛屿列表 */
    islandList: Array<{
        index: number;
        label: string;
        value: string | number;
    }> = [];

    /** 全量解分数列表 */
    iterationScoreList: Array<{
        iteration_found: number;
        combined_score: number;
    }> = [];

    /** 轮询服务 */
    pollingService: PollingService;
    /** 标记当前是否在轮询 */
    isPolling: boolean = false;

    /** 演化阶段筛选参数 - Top解显示数量 */
    evolveTopValue: number = 20;
    /** 演化阶段筛选参数 - 是否只显示TOP解及轨迹解 */
    evolveIsShowTrackSolution: boolean = false;
    /** 演化阶段筛选参数 - 选中的岛屿ID列表 */
    evolveSelectedIslands: string[] = [];

    constructor(job_id: string = '', jobsListStore?: JobsListStore) {
        this.job_id = job_id;
        this.jobsListStore = jobsListStore || null;

        // 初始化新的 Store
        this.taskExecutionStore = new TaskExecutionStore(job_id);
        this.stageDataCache = new StageDataCache();
        this.operationLoadingStore = new OperationLoadingStore();
        this.confirmDialogStore = new ConfirmDialogStore();
        this.viewStateStore = new ViewStateStore();
        this.taskInitializationStore = new TaskInitializationStore();
        // 传递 jobsListStore 给 TaskEvoRunStore
        this.taskEvoRunStore = new TaskEvoRunStore(
            job_id,
            jobsListStore?.selectedJobDetail
        );
        this.taskCodePackageStore = new TaskCodePackageStore();
        this.pollingService = new PollingService(() => this.handlePolling());

        makeObservable(this, {
            // 新的 Store 引用
            taskExecutionStore: observable,
            stageDataCache: observable,
            operationLoadingStore: observable,
            confirmDialogStore: observable,
            viewStateStore: observable,
            taskInitializationStore: observable,
            taskEvoRunStore: observable,
            taskCodePackageStore: observable,
            // 保留的字段
            report_url: observable,
            web_ide_url: observable,
            real_web_ide_url: observable,
            job_id: observable,
            filter: observable,
            stageInfo: observable,
            initialSolutionFileInfoList: observable,
            selectedInitialSolutionRowInfo: observable,
            islandList: observable,
            iterationScoreList: observable,
            coldStartProgressData: observable,
            evolveProgressData: observable,
            evolveTopValue: observable,
            evolveIsShowTrackSolution: observable,
            evolveSelectedIslands: observable,
            isPolling: observable,
            allProgressInfo: observable,
            fetchProgress: flow.bound,
            terminateTask: flow.bound,
            nextStepTask: flow.bound,
            finishedTask: flow.bound,
            getWebIdeAndReportUrl: flow.bound,
            fetchCancelJob: flow.bound,
            pauseTask: flow.bound,
            fetchStageInfo: flow.bound,
            fetchStageScore: flow.bound,
            fetchRebuildJob: flow.bound,
            continueTask: flow.bound,
            fetchStageInfoAndStageScore: flow.bound,
            getEvolveStageRunSolutions: flow.bound,
            isTerminatedOrCompletedFiled: computed,
            isTerminated: computed,
            isColdStartStage: computed,
            setWebideUrl: action.bound,
            setSelectedStageRowInfo: action.bound,
            setFilter: action.bound,
            setColdStartProgressData: action.bound,
            setEvolveProgressData: flow.bound,
            setEvolveFilterParams: action.bound,
            processingTaskResultData: action.bound,
            initTaskRunningStore: action.bound,
            initTaskRunningStageData: action.bound,
            resetInitializationFlag: action.bound,
            cancelAllJobRequests: action.bound
        });
    }

    /** 检查任务是否终止，完成，暂停，失败 停止轮询 */
    get isTerminatedOrCompletedFiled(): boolean {
        const state = this.taskExecutionStore.state;
        const stage = this.taskExecutionStore.stage;

        if (!state || !stage) {
            return true;
        }

        // 任务完成（COMPLETED）且 stage 是 REPORT，无论 page 是什么都停止轮询
        if (this.taskExecutionStore.isReportCompleted) {
            return true;
        }

        // 合并取消、失败、暂停状态的判断
        const terminatedStates = [
            TaskState.CANCELLED,
            TaskState.FAILED,
            TaskState.PAUSED,
            TaskState.TERMINATED
        ];

        return terminatedStates.includes(state);
    }

    /** 是否可以手动终止任务 */
    get isTerminated(): boolean {
        return this.taskExecutionStore.canTerminate;
    }

    /** 判断是否是冷启动阶段 */
    get isColdStartStage(): boolean {
        return this.taskExecutionStore.stage === 'COLDSTART';
    }

    /**
     * 获取任务进度
     * 任务进度数据为数组，默认取最后一条新的任务状态和进度
     */
    * fetchProgress(this: TaskRunningStore, job_id: string = '', options?: {skipStageInfo?: boolean}) {
        try {
            if (!this.prepareForFetch(job_id)) {
                return;
            }

            if (this.shouldFetchJobStatus()) {
                const res: ApiResponse = yield fetchJobStatus(this.job_id);
                const progressInfo: TaskStatusResult[] = res?.data || [];

                if (progressInfo.length === 0) {
                    this.stopPolling();
                    return;
                }

                this.allProgressInfo = progressInfo;
                yield this.processProgressData(progressInfo);
            }

            if (!options?.skipStageInfo) {
                yield this.fetchStageInfoAndStageScore();
            }
        } catch (error: unknown) {
            this.handleFetchProgressError(error);
        }
    }

    * fetchStageInfoAndStageScore(this: TaskRunningStore) {
        if (!this.hasProgressInfo()) {
            return;
        }

        if (this.shouldSkipColdStartPolling()) {
            return;
        }

        if (this.canFetchStageInfo()) {
            yield this.fetchStageInfo();

            if (this.shouldFetchScore()) {
                yield this.fetchStageScore();
            }
        }
    }

    /** 终止任务 */
    * terminateTask(this: TaskRunningStore) {
        this.operationLoadingStore.startOp('terminate');
        yield terminateJob({job_id: this.job_id});
        yield this.fetchProgress(this.job_id);
        this.startPolling();
    }

    /** 暂停任务 */
    * pauseTask(this: TaskRunningStore, jobId: string | '' = '') {
        this.operationLoadingStore.startOp('pause');
        yield pauseJob({job_id: jobId || this.job_id});
        yield this.fetchProgress();
        this.taskExecutionStore.pause();
    }

    /** 继续任务 */
    * continueTask(this: TaskRunningStore, jobId: string | '' = '') {
        this.operationLoadingStore.startOp('resume');
        yield continueJob({job_id: jobId || this.job_id});
        yield this.fetchProgress();
        yield this.startPolling();
        this.taskExecutionStore.resume();
    }

    /** 取消任务 */
    * fetchCancelJob(this: TaskRunningStore, release: boolean = true) {
        this.operationLoadingStore.startOp('cancel');
        yield cancelJob({job_id: this.job_id, release});
    }

    /** 任务进入下一阶段 */
    * nextStepTask(this: TaskRunningStore, stage: JobStageInfoName = 'EVOLVE') {
        this.operationLoadingStore.startOp('nextTask');
        try {
            yield nextJob({job_id: this.job_id, stage});
            if ([TaskStage.EVOLVE, TaskStage.REPORT as JobStageInfoName].includes(stage)) {
                this.taskExecutionStore.setProgress(stage, 0);
                this.taskExecutionStore.setState(TaskState.RUNNING);
                yield this.fetchProgress();
            }

        } finally {
            this.operationLoadingStore.endOp('nextTask');
        }
    }

    /** 任务重新构造 */
    * fetchRebuildJob(this: TaskRunningStore) {
        this.operationLoadingStore.startOp('rebuild');
        try {
            yield rebuildJob(this.job_id);
            this.taskExecutionStore.setProgress('COLDSTART', 0);
            this.taskExecutionStore.setState(TaskState.RUNNING);
            yield this.fetchProgress();
            this.viewStateStore.setHasNewSolution(false);
            this.viewStateStore.setHeroInputValue('');
        } finally {
            this.operationLoadingStore.endOp('rebuild');
        }
    }

    /** 任务完成done */
    * finishedTask(this: TaskRunningStore, jobId: string) {
        yield doneJob(jobId);
    }

    /** 获取WEB IDE和报告链接 */
    * getWebIdeAndReportUrl(this: TaskRunningStore, jobId: string, web_ide_path: string) {
        this.operationLoadingStore.startOp('webIde');
        try {
            const res: ApiResponse = yield fetchJobReport(jobId);
            /** 任务已切换，丢弃过期响应 */
            if (jobId !== this.job_id) {
                return;
            }
            const data = res?.data || {};
            /** 当任务状态接口，后端返回报告链接异常时候，从这里获取 */
            if (!this.report_url && data.report_url) {
                this.report_url = data.report_url;
            }
            /**
             * 请求被取消时 requestManager 会返回空响应，后端 webide 未就绪时也可能不返回 web_ide_url。
             * 此时不能拼接链接，否则会得到 /proxyApi/undefined 这种无效地址（iframe 会回落到当前页面），
             * 且 real_web_ide_url 变为非空后，调用方的守卫会阻止后续重试。
             */
            if (!data.web_ide_url || !web_ide_path) {
                return;
            }
            this.web_ide_url = `${window.location.origin}/proxyApi/${data.web_ide_url}?folder=${web_ide_path}/${jobId}`;
            this.real_web_ide_url = this.web_ide_url;
        } finally {
            this.operationLoadingStore.endOp('webIde');
        }
    }

    /**
     * 获取任务进度结果详情
     * @param params - 查询参数
     * @param isManual - 是否手动触发（手动触发会取消之前的请求）
     */
    * fetchStageInfo(this: TaskRunningStore, params?: fetchStageInfoParams) {
        const requestJobId = this.job_id;
        try {
            if (!requestJobId || !this.viewStateStore.stageInfoName) {
                return;
            }

            // 构建请求参数
            const infoParams: any = {
                top: 20,
                ...params,
                ...(this.filter.island_id.length >= 1 && {
                    filter: this.filter.island_id
                })
            };

            // 演化阶段自动应用筛选条件（仅用于轮询）
            if (
                this.viewStateStore.stageInfoName === TaskStage.EVOLVE
                && !params
            ) {
                buildEvolveStageParams(
                    infoParams,
                    this.evolveTopValue,
                    this.evolveIsShowTrackSolution,
                    this.evolveSelectedIslands
                );
            }

            const stageInfoName = params?.stage
                ? params.stage
                : this.viewStateStore.stageInfoName;
            const res: ApiResponse = yield getStageResultInfo(
                requestJobId,
                stageInfoName,
                infoParams
            );

            if (requestJobId !== this.job_id || requestJobId !== jobIdSyncService.getJobIdFromUrl()) {
                return;
            }

            if (!res.data) {
                this.taskExecutionStore.setError('获取任务进度结果详情失败！');
                return;
            }

            yield (this.stageInfo = res.data);
            this.processingTaskResultData(params?.stage);
        } catch (error: unknown) {
            // 处理请求取消错误
            const err = error as any;
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return;
            }
            handleRequestError({error});
        } finally {
            // fetchStageInfo 不需要特殊的 loading 清理
        }
    }

    /**
     * 获取阶段全量解分数
     */
    * fetchStageScore(
        this: TaskRunningStore,
        stageInfoName: STAGE_TYPE | '' = this.viewStateStore.stageInfoName
    ) {
        const requestJobId = this.job_id;
        try {
            if (!requestJobId || requestJobId !== jobIdSyncService.getJobIdFromUrl()) {
                return;
            }

            const res: ApiResponse = yield getStageScore(
                requestJobId,
                stageInfoName
            );
            if (requestJobId !== this.job_id || requestJobId !== jobIdSyncService.getJobIdFromUrl()) {
                return;
            }
            if (!res.data) {
                return;
            }
            // 确保 iterationScoreList 始终是数组
            this.iterationScoreList = Array.isArray(res.data) ? res.data : [];
        } catch (error: unknown) {
            console.error('获取全量解分数失败:', error);
        }
    }

    /** 演化续跑，优质解普通解列表 */
    * getEvolveStageRunSolutions(this: TaskRunningStore) {
        // 防止并发请求
        if (this.operationLoadingStore.isLoadingEvolveStageRunSolutions) {
            return;
        }

        try {
            this.operationLoadingStore.startOp('evolveStageRunSolutions');
            this.viewStateStore.setStageInfoName(TaskStage.SELECT);
            yield this.fetchStageInfo({stage: TaskStage.SELECT});
        } finally {
            this.operationLoadingStore.endOp('evolveStageRunSolutions');
        }
    }

    /** 处理任务结果数据 */
    processingTaskResultData(stage = '') {
        // 安全检查：确保 stageInfo 存在
        if (!this.stageInfo) {
            return;
        }

        // 处理解决方案数据
        const stageInfoName = this.viewStateStore.stageInfoName as JobStageInfoName;
        const {solutions: initialSolutionFileInfoList, initialSolutionsArray} = normalizeSolutions(this.stageInfo, stageInfoName);
        const filterInitialSolutionList = applyStageFilter(
            initialSolutionFileInfoList,
            stageInfoName,
            this.filter
        );

        // 如果是续跑页面，处理优质解数据
        if (stage === 'SELECT') {
            /** 如果是续跑页面, 系统推荐解取前15条 */
            this.taskEvoRunStore.setSystemRecommendedSolution(
                initialSolutionFileInfoList.filter((_, index) => index < 15)
            );
            // 设置全部节点解数据
            this.taskEvoRunStore.setAllRecommendedSolutions(initialSolutionsArray);
            // 设置续跑勾选的解数据
            this.taskEvoRunStore.setRecommendedSolutions(initialSolutionsArray);
        }

        // 更新阶段数据
        this.updateStageData(
            filterInitialSolutionList,
            initialSolutionFileInfoList
        );

        // 更新解决方案列表 (仅保留筛选后的前20个)
        this.initialSolutionFileInfoList = filterInitialSolutionList.filter(
            (_, index) => index < 20
        );

        // 生成去重的岛屿列表
        this.islandList = generateUniqueIslandList(
            this.stageInfo?.island_score || {}
        );
    }

    /**
     * 初始化任务运行 Store
     * 委托给 TaskInitializationStore 处理
     */
    initTaskRunningStore(job_id: string) {
        if (job_id !== this.job_id && this.job_id) {
            this.cancelAllJobRequests(this.job_id);
        }

        // 更新 TaskEvoRunStore 中的 selectedJobDetail 引用
        this.taskEvoRunStore.updateSelectedJobDetail(
            this.jobsListStore?.selectedJobDetail || null
        );

        this.taskInitializationStore.initTaskRunningStore(this, job_id);
    }

    /**
     * 初始化任务执行结果数据
     * 委托给 TaskInitializationStore 处理
     */
    initTaskRunningStageData() {
        this.taskInitializationStore.initTaskRunningStageData(this);
    }

    /**
     * 重置初始化状态（切换任务时调用）
     * 委托给 TaskInitializationStore 处理
     */
    resetInitializationFlag() {
        this.taskInitializationStore.reset();
    }

    /** 开始轮询 */
    startPolling(job_id: string = this.job_id) {
        // 防重：如果 job_id 变化，停止旧的轮询
        if (this.job_id !== job_id) {
            this.stopPolling();
        }
        // 防重：如果已经在轮询，不重复启动
        if (this.isPolling) {
            return;
        }

        if (!this.job_id) {
            return;
        }
        this.isPolling = true;
        this.pollingService.start();
    }

    /** 停止轮询 */
    stopPolling() {
        if (!this.isPolling) {
            return; // 未轮询，直接返回
        }

        this.pollingService.stop();
        this.isPolling = false;
    }

    /**
     * 取消当前任务的所有进行中请求
     * 委托给 TaskInitializationStore 处理
     */
    cancelAllJobRequests(jobIds?: string | string[]) {
        this.taskInitializationStore.cancelAllJobRequests(jobIds || this.job_id);
    }

    /** 获取webide 前缀path */
    setWebideUrl(path: string): void {
        this.viewStateStore.setProcessActivTab('2');
        /** web_ide_url 还没拿到时不能拼接，否则会得到相对地址导致 iframe 加载当前页面 */
        if (!this.web_ide_url) {
            return;
        }
        const filePath = `&payload=[["openFile", "vscode-remote://${path}"]]`;
        this.real_web_ide_url = `${this.web_ide_url}${filePath}`;
    }

    /** 解的筛选条件 */
    setFilter(data: Partial<IFilter>) {
        let params = data;
        if (this.viewStateStore.stageInfoName === TaskStage.COLDSTART) {
            if (data.is_initial_solution) {
                params = {
                    ...params,
                    is_initial_solution: !!data.is_initial_solution
                };
            }
        }
        Object.assign(this.filter, params);
    }

    /** 设置冷启动，迭代演化阶段选中的解 */
    setSelectedStageRowInfo(val: InitialSolutionFileInfo | null) {
        this.selectedInitialSolutionRowInfo = val;
    }

    /** 设置演化阶段筛选参数 */
    setEvolveFilterParams(params: {
        topValue?: number;
        isShowTrackSolution?: boolean;
        islands?: string[];
    }) {
        if (params.topValue !== undefined) {
            this.evolveTopValue = params.topValue;
        }
        if (params.isShowTrackSolution !== undefined) {
            this.evolveIsShowTrackSolution = params.isShowTrackSolution;
        }
        if (params.islands !== undefined) {
            this.evolveSelectedIslands = params.islands;
        }
    }

    /** 保存冷启动状态数据 */
    setColdStartProgressData(progressInfo: TaskStatusResult[]) {
        if (progressInfo[0].stage === TaskStage.COLDSTART) {
            this.coldStartProgressData = progressInfo[0];
            const info = progressInfo[0];

            // 更新 StageDataCache 中的冷启动数据
            if (info.stage_data) {
                const coldStartData = {
                    current_diversity: info.stage_data.current_diversity,
                    initial_solution_number: info.stage_data.current_solution
                };
                this.coldStartProgressData = {
                    ...info,
                    ...info.stage_data,
                    ...coldStartData
                };
            }
            if (
                this.taskExecutionStore.isColdCompleted
                && !this.confirmDialogStore.showSolutionModal
            ) {
                this.stopPolling();
                // 展示进入下一阶段询问弹窗
                this.confirmDialogStore.setShowSolutionModal(true);
                this.startPolling();
            }
        }
    }

    /*
     * 保存演化状态数据
     * 演化阶段完成后，报告阶段也需要完成，才可以展示进入下一阶段询问弹窗 isEvolveCompleted
     * */
    * setEvolveProgressData(progressInfo: TaskStatusResult[]) {
        if (
            progressInfo.length > 1
                && progressInfo[0].stage === TaskStage.COLDSTART
                && progressInfo[1].stage === TaskStage.EVOLVE
        ) {
            this.evolveProgressData = progressInfo[1];
        } else if (progressInfo[0].stage === TaskStage.EVOLVE) {
            this.evolveProgressData = progressInfo[0];
        }

        // 演化阶段完成，自动调用 nextStepTask 进入报告阶段
        if (this.taskExecutionStore.shouldAutoEnterReport) {
            yield this.nextStepTask(TaskStage.REPORT);
        }

        // 新需求：当任务状态为COMPLETED且stage和page都为REPORT时，自动切换到结果洞察tab并弹出弹窗
        if (this.taskExecutionStore.shouldShowReportModal) {
            // 自动切换到结果洞察tab
            if (this.viewStateStore.stageInfoName !== TaskStage.REPORT) {
                this.viewStateStore.setStageInfoName(TaskStage.REPORT);
            }
            // 停止轮询
            this.stopPolling();
        }
    }

    updateTaskActionLoading() {
        const state = this.taskExecutionStore.state;
        // 如果终止状态了，终止loading取消
        if (
            (state === TaskState.CANCELLED
                || state === TaskState.FAILED
                || state === TaskState.TERMINATED
                || this.taskExecutionStore.isFailed)
            && this.operationLoadingStore.isTerminating
        ) {
            this.operationLoadingStore.endOp('pause');
            this.operationLoadingStore.endOp('resume');
            this.operationLoadingStore.endOp('terminate');
            this.stopPolling();
        }
        // 如果暂停状态了，暂停loading取消
        if (
            (this.taskExecutionStore.isPaused
                || this.taskExecutionStore.isFailed)
            && this.operationLoadingStore.isPausing
        ) {
            this.operationLoadingStore.endOp('pause');
            this.stopPolling();
        }

        // 如果继续任务完成，清除loading状态
        if (
            this.operationLoadingStore.isResuming
            && !this.taskExecutionStore.isPaused
        ) {
            this.operationLoadingStore.endOp('resume');
        }
    }

    /** 更新阶段数据 */
    private updateStageData(
        filterInitialSolutionList: InitialSolutionFileInfo[],
        initialSolutionFileInfoList: InitialSolutionFileInfo[]
    ) {
        const stageInfoName = this.viewStateStore.stageInfoName;
        if (!stageInfoName) {
            return;
        }

        const stageData = buildStageData(
            this.stageInfo,
            stageInfoName as JobStageInfoName,
            filterInitialSolutionList,
            initialSolutionFileInfoList,
            (seconds: number) => String(formatSecondsToHMS(seconds))
        );

        if (stageData) {
            this.stageDataCache.update(stageInfoName as any, stageData);
        }
    }

    /** 持有 JobsListStore 的引用 (用于访问任务列表信息) */
    private readonly jobsListStore: JobsListStore | null = null;

    /**
     * 处理进度信息
     * @private
     */
    private processProgressData(progressInfo: TaskStatusResult[]) {
        if (!progressInfo.length) {
            return;
        }

        const lastData = progressInfo[progressInfo.length - 1];

        // 更新任务执行状态
        this.updateExecutionState(lastData);
        // 同步视图状态
        this.syncViewState();
        // 更新阶段进度数据
        this.updateStageProgress(progressInfo);
        // 处理失败和取消状态
        this.handleFailedState(lastData);
    }

    private updateExecutionState(data: TaskStatusResult) {
        this.taskExecutionStore.setStateFromLegacy(data.status);
        if (
            data.stage
            && [TaskStage.COLDSTART, TaskStage.EVOLVE, TaskStage.REPORT].includes(
                data.stage as TaskStage
            )
        ) {
            const percentage = getProgressPercentage(data.progress);
            this.taskExecutionStore.setProgress(
                data.stage as TaskStage,
                percentage
            );
        }

        if (data.page) {
            this.taskExecutionStore.setJobRunningPage(data.page);
        }

        // 更新报告链接
        if (data.report_url && this.taskExecutionStore.isCompleted) {
            this.report_url = data.report_url;
        }
    }

    /**
     * 同步视图状态
     * 防止轮询接口覆盖手动点击的tab
     * SELECT 也是演化阶段
     */
    private syncViewState() {
        // 如果已有视图状态，不覆盖（防止轮询接口覆盖手动点击的tab）
        if (this.viewStateStore.stageInfoName) {
            return;
        }

        const {stage, jobRunningPage} = this.taskExecutionStore;
        if (!stage) {
            return;
        }

        // 兼容老的任务，只有两个阶段：COLDSTART 和 EVOLVE（REPORT是EVOLVE的完成阶段）
        if (stage === TaskStage.EVOLVE && jobRunningPage === TaskStage.REPORT) {
            this.viewStateStore.setStageInfoName(TaskStage.REPORT);
            return;
        }
        // 非 SELECT 阶段，直接使用当前阶段
        if (stage !== TaskStage.SELECT) {
            this.viewStateStore.setStageInfoName(stage);
        }
    }

    private updateStageProgress(progressInfo: TaskStatusResult[]) {
        // 更新任务操作 loading 状态
        this.updateTaskActionLoading();
        // 更新冷启动和演化阶段进度数据
        this.setColdStartProgressData(progressInfo);
        this.setEvolveProgressData(progressInfo);
    }

    /**
     * 处理失败和取消状态
     */
    private handleFailedState(data: TaskStatusResult) {
        if (this.taskExecutionStore.isCancelled) {
            this.confirmDialogStore.setTerminateModalVisible(false);
            this.confirmDialogStore.reset();
        }

        if (this.taskExecutionStore.isFailed) {
            if (this.confirmDialogStore.showSolutionModal) {
                this.confirmDialogStore.setShowSolutionModal(false);
            }

            const errorMessage =
                data.message
                || `获取任务进度：${this.taskExecutionStore.isInitialingPage ? '可行性判定失败！' : '任务异常失败！'}`;
            this.taskExecutionStore.setError(errorMessage);
            this.stopPolling();
        }
    }

    private handleProgressError(error: unknown) {
        const errorMessage =
            error instanceof Error
                ? `获取任务进度: ${error.message}`
                : '获取任务进度中发生未知错误';
        this.taskExecutionStore.setError(errorMessage);
        throw error;
    }

    /**
     * 准备获取进度的前置检查
     * @returns false 表示无需继续执行
     */
    private prepareForFetch(job_id: string): boolean {
        this.job_id = job_id || this.job_id;
        this.taskExecutionStore.setError('');

        if (!this.job_id && this.isPolling) {
            this.stopPolling();
            return false;
        }
        return true;
    }

    /**
     * 判断是否需要获取任务状态
     * 何时需要获取:
     * 1. 冷启动未完成
     * 2. 正在演化阶段
     * 3. 任务正在运行
     * 4. 有操作正在进行(暂停/恢复/终止)
     */
    private shouldFetchJobStatus(): boolean {
        return (
            !this.taskExecutionStore.isColdCompleted
            || this.taskExecutionStore.stage === 'EVOLVE'
            || this.taskExecutionStore.state === TaskState.RUNNING
            || this.hasActiveOperation()
        );
    }

    /**
     * 检查是否有操作正在进行
     */
    private hasActiveOperation(): boolean {
        return (
            this.operationLoadingStore.isTerminating
            || this.operationLoadingStore.isPausing
            || this.operationLoadingStore.isResuming
        );
    }

    /**
     * 处理获取进度时的错误
     */
    private handleFetchProgressError(error: any) {
        if (
            (error?.status === 404 || error?.response?.status === 404)
            && this.isPolling
        ) {
            this.stopPolling();
            return;
        }
        this.handleProgressError(error);
    }

    /**
     * 检查是否有进度信息
     */
    private hasProgressInfo(): boolean {
        return this.allProgressInfo.length > 0;
    }

    /**
     * 判断是否应该跳过冷启动阶段的轮询
     * 场景: 任务已进入演化阶段,但用户正在查看已完成的冷启动阶段
     */
    private shouldSkipColdStartPolling(): boolean {
        if (this.allProgressInfo.length <= 1) {
            return false;
        }

        const currentStage = this.allProgressInfo[0];
        const nextStage = this.allProgressInfo[1];

        // 任务已进入演化阶段
        if (nextStage.stage !== TaskStage.EVOLVE) {
            return false;
        }

        // 用户正在查看已完成的冷启动阶段
        return (
            currentStage.stage === TaskStage.COLDSTART
            && currentStage.status === 'COMPLETED'
            && this.viewStateStore.stageInfoName === 'COLDSTART'
        );
    }

    /**
     * 判断是否可以获取阶段信息
     * 只有在非草稿/初始化/创建状态时才获取
     */
    private canFetchStageInfo(): boolean {
        const state = this.taskExecutionStore.state;
        return (
            state !== TaskState.DRAFT
                && !this.taskExecutionStore.isInitialingPage

        );
    }

    /**
     * 判断是否应该获取全量解分数
     * 只在迭代演化阶段获取
     */
    private shouldFetchScore(): boolean {
        return this.viewStateStore.stageInfoName === TaskStage.EVOLVE;
    }

    /**
     * 处理轮询逻辑
     * @private
     */
    private async handlePolling(): Promise<void> {
        // 每次轮询时动态获取最新的 job_id
        const currentJobId = this.job_id;

        // 检查1: job_id 和地址栏 jobId 一致性
        if (
            !currentJobId
            || currentJobId !== jobIdSyncService.getJobIdFromUrl()
        ) {
            return;
        }

        await this.fetchProgress(currentJobId);
        /** 如果是暂停后又继续的操作不停止轮询 */
        if (
            this.isTerminatedOrCompletedFiled
            && !this.operationLoadingStore.isResuming
            && !this.operationLoadingStore.isTerminating
        ) {
            this.stopPolling();
        }
    }
}
